/** One row of the AI models table, its problem note and its Test outcome dialog. */

import { useState } from 'react';
import { AlertTriangle, Pencil, Zap } from 'lucide-react';

import { useSaveModel, useTestModel } from '@/api/queries';
import type { AiModel } from '@/api/types';
import { formatUsd } from '@/ui/format';

import { testTooltip, type RowAccess } from './modelList';
import { TestResultDialog } from './TestResultDialog';

type TestMutation = ReturnType<typeof useTestModel>;

export function ModelRow({
  model,
  hostingLabel,
  access,
  columns,
  onEdit,
}: {
  model: AiModel;
  hostingLabel: string;
  access: RowAccess;
  columns: number;
  onEdit: () => void;
}) {
  const test = useTestModel();
  const [testing, setTesting] = useState(false);
  const problem = model.enabled ? model.unusable_reason : null;

  return (
    <>
      <tr>
        <td>
          <b>{model.display_name}</b>
          {problem && (
            <span className="sub status-warn row" title={problem}>
              <AlertTriangle aria-hidden="true" /> Needs attention
            </span>
          )}
        </td>
        <td>{hostingLabel}</td>
        <td className="mono">{model.model_id}</td>
        <td className="num">{formatUsd(model.price_input)}</td>
        <td className="num">{formatUsd(model.price_output)}</td>
        <td>
          <EnabledCell model={model} manage={access.manage} />
        </td>
        {columns === 7 && (
          <td>
            <RowActions
              model={model}
              access={access}
              test={test}
              onTest={() => {
                setTesting(true);
                test.mutate(model.key);
              }}
              onEdit={onEdit}
            />
          </td>
        )}
      </tr>
      {problem && (
        <tr className="row-note">
          <td colSpan={columns}>
            <span className="status-warn row"><AlertTriangle aria-hidden="true" /> {problem}</span>
          </td>
        </tr>
      )}
      {testing && (
        <TestResultDialog
          model={model}
          pending={test.isPending}
          result={test.data}
          error={test.error}
          onClose={() => {
            setTesting(false);
            test.reset();
          }}
        />
      )}
    </>
  );
}

/** A switch for managers, plain text for everyone else. */
function EnabledCell({ model, manage }: { model: AiModel; manage: boolean }) {
  const save = useSaveModel();
  if (!manage) return <>{model.enabled ? 'Enabled' : 'Disabled'}</>;
  return (
    <label className="switch" data-tooltip={model.enabled ? 'Enabled. Switch off to disable.' : 'Disabled. Switch on to enable.'}>
      <input
        type="checkbox"
        checked={model.enabled}
        disabled={save.isPending}
        aria-label={`${model.enabled ? 'Disable' : 'Enable'} ${model.display_name}`}
        onChange={(e) => save.mutate({ key: model.key, body: { enabled: e.target.checked } })}
      />
      <span className="switch-track" aria-hidden="true" />
    </label>
  );
}

function RowActions({
  model,
  access,
  test,
  onTest,
  onEdit,
}: {
  model: AiModel;
  access: RowAccess;
  test: TestMutation;
  onTest: () => void;
  onEdit: () => void;
}) {
  return (
    <div className="row-actions">
      {/* A disabled button gets no hover, so the tooltip sits on a wrapper. */}
      {access.test && (
        <span className="row-action-wrap" data-tooltip={testTooltip(model, test.isPending)}>
          <button
            type="button"
            className="btn btn-secondary btn-icon"
            aria-label={`Test ${model.display_name}`}
            disabled={test.isPending || !!model.unusable_reason}
            onClick={onTest}
          >
            <Zap aria-hidden="true" />
          </button>
        </span>
      )}
      {access.manage && (
        <button type="button" className="btn btn-secondary btn-icon" data-tooltip="Edit" aria-label={`Edit ${model.display_name}`} onClick={onEdit}>
          <Pencil aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
